import { useCallback, useEffect, useMemo, useState } from "react";
import { listRows, updateRow } from "@/lib/adminApi";
import {
  VERIFICATION_CATEGORIES,
  VERIFICATION_OUTCOME,
  VERIFICATION_WORKFLOW,
  metaFor,
  optionsFrom,
  type FarmVerification,
} from "@/lib/adminModules";
import {
  DataTable,
  DetailList,
  Drawer,
  EmptyState,
  ErrorBanner,
  Field,
  FilterBar,
  LoadingState,
  PageHeader,
  SearchInput,
  Select,
  StatCards,
  StatusPill,
  fieldInput,
  type Column,
} from "@/pages/admin/components/AdminUI";
import { formatDate } from "@/pages/admin/format";
import { useAdminToast } from "@/hooks/useAdminToast";

function outcomeFor(total: number): string {
  if (total >= 80) return "pilot_eligible";
  if (total >= 65) return "improvement";
  return "hold";
}

export default function AdminVerification() {
  const [rows, setRows] = useState<FarmVerification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [outcomeFilter, setOutcomeFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const { notify, node } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listRows<FarmVerification>("farm_verifications", "total_score"));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load verifications.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setScore = async (id: number, key: string, value: number) => {
    const current = rows.find((row) => row.id === id);
    if (!current) return;
    const draft = { ...current, [key]: value } as FarmVerification;
    const total = VERIFICATION_CATEGORIES.reduce(
      (sum, cat) => sum + Number((draft as unknown as Record<string, number>)[cat.key] || 0),
      0,
    );
    const outcome = outcomeFor(total);
    setRows((list) =>
      list.map((row) => (row.id === id ? { ...draft, total_score: total, outcome } : row)),
    );
    try {
      await updateRow("farm_verifications", id, { [key]: value, total_score: total, outcome });
    } catch {
      setError("We couldn't save that score. Please try again.");
      load();
    }
  };

  const update = async (id: number, patch: Partial<FarmVerification>, message?: string) => {
    setRows((list) => list.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    try {
      await updateRow("farm_verifications", id, patch);
      if (message) notify(message);
    } catch {
      setError("We couldn't save that change. Please try again.");
      load();
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (outcomeFilter !== "all" && row.outcome !== outcomeFilter) return false;
      if (!query) return true;
      return row.farm_name.toLowerCase().includes(query);
    });
  }, [rows, search, statusFilter, outcomeFilter]);

  const selected = rows.find((row) => row.id === selectedId) || null;

  const columns: Column<FarmVerification>[] = [
    {
      key: "farm",
      header: "Farm",
      primary: true,
      render: (row) => (
        <span className="text-sm font-semibold text-foreground-950 whitespace-nowrap">
          {row.farm_name}
        </span>
      ),
    },
    {
      key: "score",
      header: "Total score",
      render: (row) => (
        <div className="flex items-center gap-2">
          <div className="w-16 h-1.5 rounded-full bg-background-200 overflow-hidden">
            <div className="h-full rounded-full bg-primary-500" style={{ width: `${row.total_score}%` }}></div>
          </div>
          <span className="text-sm font-semibold text-foreground-800">{row.total_score}</span>
        </div>
      ),
    },
    { key: "outcome", header: "Outcome", render: (row) => <StatusPill {...metaFor(VERIFICATION_OUTCOME, row.outcome)} /> },
    { key: "status", header: "Status", render: (row) => <StatusPill {...metaFor(VERIFICATION_WORKFLOW, row.status)} /> },
    {
      key: "critical",
      header: "Critical issue",
      render: (row) =>
        row.critical_issue ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-accent-900">
            <i className="ri-alert-line"></i> Yes
          </span>
        ) : (
          <span className="text-xs text-foreground-500">None</span>
        ),
    },
    {
      key: "inspected",
      header: "Inspected",
      render: (row) => (
        <span className="text-xs text-foreground-500 whitespace-nowrap">{formatDate(row.inspected_at)}</span>
      ),
    },
    {
      key: "next",
      header: "Next audit",
      render: (row) => (
        <span className="text-xs text-foreground-500 whitespace-nowrap">{formatDate(row.next_audit)}</span>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Farm Network"
        title="Farm Verification"
        subtitle="Score every farm across safety, experience, hygiene, accessibility, host capability, facilities, uniqueness and environment — out of 100."
      />

      <StatCards
        stats={[
          { label: "Verifications", value: rows.length, icon: "ri-shield-check-line", tone: "primary" },
          {
            label: "Pilot eligible",
            value: rows.filter((row) => row.outcome === "pilot_eligible").length,
            icon: "ri-checkbox-circle-line",
            tone: "primary",
          },
          {
            label: "Improvement required",
            value: rows.filter((row) => row.outcome === "improvement").length,
            icon: "ri-tools-line",
            tone: "accent",
          },
          {
            label: "On hold",
            value: rows.filter((row) => row.outcome === "hold").length,
            icon: "ri-pause-circle-line",
            tone: "neutral",
          },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5 space-y-5">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Search by farm name…" />
          <Select
            label="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[{ value: "all", label: "All statuses" }, ...optionsFrom(VERIFICATION_WORKFLOW)]}
          />
          <Select
            label="Outcome"
            value={outcomeFilter}
            onChange={setOutcomeFilter}
            options={[{ value: "all", label: "All outcomes" }, ...optionsFrom(VERIFICATION_OUTCOME)]}
          />
          <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">
            {filtered.length} farms
          </span>
        </FilterBar>

        {loading ? (
          <LoadingState label="Loading verifications…" />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="ri-shield-check-line"
            title={rows.length === 0 ? "No verifications yet" : "No verifications match"}
            message="Farms awaiting verification will appear here."
          />
        ) : (
          <DataTable columns={columns} rows={filtered} onRowClick={(row) => setSelectedId(row.id)} />
        )}
      </div>

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        title={selected?.farm_name || "Verification"}
        subtitle="Verification workspace"
        footer={
          selected ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => update(selected.id, { status: "passed", outcome: outcomeFor(selected.total_score) }, "Verification passed.")}
                className="inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-check-double-line"></i> Approve
              </button>
              <button
                type="button"
                onClick={() => update(selected.id, { status: "improvement" }, "Improvement requested.")}
                className="inline-flex items-center gap-2 border border-background-300 text-foreground-800 text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-tools-line"></i> Request changes
              </button>
            </div>
          ) : null
        }
      >
        {selected ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill {...metaFor(VERIFICATION_WORKFLOW, selected.status)} />
              <StatusPill {...metaFor(VERIFICATION_OUTCOME, selected.outcome)} />
            </div>

            <div className="flex items-center justify-between gap-3 bg-background-100 border border-background-200 rounded-md p-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500">
                  Total score
                </p>
                <p className="font-heading text-3xl font-semibold text-foreground-950">
                  {selected.total_score}
                  <span className="text-base text-foreground-500">/100</span>
                </p>
              </div>
              <span className="text-xs text-foreground-600 text-right max-w-[180px]">
                80+ pilot eligible · 65–79 improvement · below 65 hold
              </span>
            </div>

            <Field label="Score breakdown">
              <div className="space-y-3">
                {VERIFICATION_CATEGORIES.map((cat) => {
                  const value = Number((selected as unknown as Record<string, number>)[cat.key] || 0);
                  return (
                    <div key={cat.key}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-foreground-700">{cat.label}</span>
                        <span className="font-semibold text-foreground-900">
                          {value}/{cat.max}
                        </span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={cat.max}
                        value={value}
                        onChange={(event) => setScore(selected.id, cat.key, Number(event.target.value))}
                        className="w-full cursor-pointer accent-primary-500"
                      />
                    </div>
                  );
                })}
              </div>
            </Field>

            <DetailList
              items={[
                { icon: "ri-calendar-line", label: "Inspected", value: formatDate(selected.inspected_at) },
                { icon: "ri-calendar-check-line", label: "Next audit", value: formatDate(selected.next_audit) },
              ]}
            />

            <Field label="Status">
              <select
                value={selected.status}
                onChange={(event) => update(selected.id, { status: event.target.value })}
                className={fieldInput}
              >
                {optionsFrom(VERIFICATION_WORKFLOW).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>

            <label className="flex items-center gap-3 bg-accent-100 border border-accent-300 rounded-md px-4 py-3 cursor-pointer">
              <input
                type="checkbox"
                checked={selected.critical_issue}
                onChange={(event) => update(selected.id, { critical_issue: event.target.checked })}
                className="w-4 h-4 cursor-pointer accent-accent-600"
              />
              <span className="text-sm text-accent-900 font-medium">
                Critical safety issue (overrides the score)
              </span>
            </label>

            <Field label="Notes">
              <textarea
                rows={3}
                value={selected.notes || ""}
                onChange={(event) =>
                  setRows((list) =>
                    list.map((row) => (row.id === selected.id ? { ...row, notes: event.target.value } : row)),
                  )
                }
                onBlur={() => update(selected.id, { notes: selected.notes })}
                placeholder="Findings, corrective actions, evidence references…"
                className="w-full bg-background-100 border border-background-300 rounded-md px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
              ></textarea>
            </Field>
          </>
        ) : null}
      </Drawer>

      <p className="text-xs text-foreground-500 mt-5">
        <i className="ri-information-line"></i> Scores save as you adjust them.
      </p>
      {node}
    </div>
  );
}